<script lang="ts" module>
	export type OnChange = (checked: CheckboxValue) => void;
</script>

<script lang="ts">
	import classNames from 'classnames';
	import { twMerge } from 'tailwind-merge';
	import { MinusIcon } from '../../assets/icons/MinusIcon';
	import { TickIcon } from '../../assets/icons/TickIcon';
	import type { CheckboxValue } from './types';

	let {
		checked = $bindable(false),
		name = undefined,
		onChange = () => void 0,
		id = undefined,
		ariaLabel = undefined,
		error = undefined,
		disabled = undefined,
		dataCy,
		size = 'md',
		classes = {},
		valueAfterIntermediate = true,
		...rest
	}: Props = $props();

	interface Props {
		valueAfterIntermediate?: boolean;
		checked?: CheckboxValue;
		name?: string | undefined;
		onChange?: OnChange;
		id?: string | undefined;
		/** Accessible name for the checkbox — required whenever no visible label wraps it. */
		ariaLabel?: string | undefined;
		error?: boolean | undefined;
		disabled?: boolean | undefined;
		dataCy: string;
		size?: 'sm' | 'md';
		classes?: { icon?: string };
		class?: string;
	}

	let getAriaChecked = $derived(() => {
		if (checked === 'indeterminate') return 'mixed';

		return checked;
	});

	let indeterminate = $derived(checked === 'indeterminate');

	const handleClick = (e: Event) => {
		e.preventDefault();

		if (disabled) return;

		if (checked === 'indeterminate') {
			checked = valueAfterIntermediate;
		} else {
			checked = !checked;
		}

		onChange?.(checked);
	};

	let dataCyPrefix = $derived(`${dataCy}-checkbox`);

	let iconClasses = $derived(
		twMerge([
			classNames(
				'flex appearance-none items-center justify-center rounded-sm border border-solid',
				{
					'border-gray-400': disabled,
					'border-gray-500': !disabled && !indeterminate && !checked,
					'border-gray-900': !disabled && (indeterminate || checked),
					'border-red-400': error,
					'group-hover:border-gray-900': !error && !disabled,
					'bg-red-400': error && (indeterminate || checked),
					'bg-gray-400': disabled && (indeterminate || checked),
					'bg-gray-900': !disabled && (indeterminate || checked),
					'h-[14px]': size === 'sm',
					'w-[14px]': size === 'sm',
					'h-[18px]': size === 'md',
					'w-[18px]': size === 'md'
				}
			),
			classes.icon
		])
	);
</script>

<button
	{id}
	onclick={handleClick}
	class={twMerge(
		'group flex h-[20px] w-[20px] cursor-pointer items-center justify-center rounded-sm',
		rest.class
	)}
	role="checkbox"
	data-cy={dataCyPrefix}
	aria-checked={getAriaChecked()}
	aria-label={ariaLabel}
	aria-disabled={disabled}
>
	<span class={iconClasses} data-cy={`${dataCyPrefix}-icon`}>
		{#if checked === 'indeterminate'}
			<MinusIcon color="gray-0" />
		{:else if checked}
			<TickIcon color="gray-0" />
		{/if}
	</span>

	<input
		{name}
		data-cy={`${dataCyPrefix}-input`}
		type="checkbox"
		aria-hidden="true"
		hidden
		tabindex="-1"
		class="pointer-events-none absolute m-0 translate-x-[-100%] opacity-0"
	/>
</button>
